import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  EmbeddedViewRef,
  Injector,
  Input,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { IonicModule, ModalController } from '@ionic/angular';
import { IndFilterMenuComponent } from '@omni/components/shared/ind-filter-menu/ind-filter-menu';
import { Subject } from 'rxjs';
import { AgendaFooterService } from '@omni/services/footer/agenda-footer.service';
import { find, isEqual, isObject } from 'lodash-es';

interface PanelButton {
  key: string;
  label?: string;
  icon?: string;
  iconName?: string;
  primary?: boolean;
  disabled?: boolean;
  action?: (component: any) => Promise<boolean | void> | boolean | void;
  selected?: boolean;
  header?: boolean;
  align?: 'left' | 'right';
  hidden?: boolean;
}

function normalizeAndStringify(obj: any, omitKeys: string[] = []) {
  if (Array.isArray(obj)) {
    return `[${obj
      .map((o) => normalizeAndStringify(o, omitKeys))
      .sort()
      .join(',')}]`;
  } else if (isObject(obj)) {
    return `{${Object.keys(obj)
      .filter((key) => !omitKeys.includes(key))
      .sort()
      .map((key) => `"${key}":${normalizeAndStringify(obj[key], omitKeys)}`)
      .join(',')}}`;
  }
  return JSON.stringify(obj);
}
@Component({
  selector: 'base-panel',
  standalone: true,
  imports: [CommonModule, IonicModule],
  templateUrl: './base-panel.component.html',
  styleUrl: './base-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BasePanelComponent {
  trackById(index: number, item: any) { return item?.id || item?.ID || index; }

  @ViewChild('container', { read: ViewContainerRef, static: true })
  container!: ViewContainerRef;
  @Input() title: string;
  @Input() content:
    | { component: any; inputs?: { [key: string]: any } }
    | { template: TemplateRef<any>; context?: { [key: string]: any } };
  leftFooterButtons: PanelButton[] = [];
  rightFooterButtons: PanelButton[] = [];
  @Input() set buttons(buttons: PanelButton[]) {
    this.leftFooterButtons =
      buttons?.filter(
        (button) => !button.header && button.align === 'left' && !button.hidden,
      ) || [];
    this.rightFooterButtons =
      buttons?.filter(
        (button) => !button.header && button.align !== 'left' && !button.hidden,
      ) || [];
    this.leftHeaderButtons =
      buttons?.filter(
        (button) => button.header && button.align === 'left' && !button.hidden,
      ) || [];
    this.rightHeaderButtons =
      buttons?.filter(
        (button) => button.header && button.align !== 'left' && !button.hidden,
      ) || [];
  }
  leftHeaderButtons: PanelButton[] = [];
  rightHeaderButtons: PanelButton[] = [];
  private componentRef: ComponentRef<any> | null = null;
  private embeddedViewRef: EmbeddedViewRef<any> | null = null;
  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly modalCtrl: ModalController,
    private readonly _cd: ChangeDetectorRef,
    private readonly footerService: AgendaFooterService,
  ) {}

  ngAfterViewInit() {
    if (this.content && 'component' in this.content) {
      this.componentRef = this.container.createComponent(
        this.content.component,
        {
          injector: Injector.create({
            providers: [
              {
                provide: BasePanelComponent,
                useValue: this,
              }
            ],
            parent: this.container.injector,
          })
        }
      );
      this.embeddedViewRef = this.componentRef.hostView as EmbeddedViewRef<any>;
      this.componentRef.location.nativeElement.setAttribute('slot', 'fixed');
      if (this.content.inputs) {
        for (const key of Object.keys(this.content.inputs)) {
          this.componentRef.setInput(key, this.content.inputs[key]);
        }
      }
      this.initFilterMenuComponent();
    } else if (this.content && 'template' in this.content) {
      this.embeddedViewRef = this.container.createEmbeddedView(this.content.template, this.content.context);
      this.embeddedViewRef.context.self = this.embeddedViewRef;
      this.embeddedViewRef.detectChanges();
    } else {
      throw new Error('No content or component provided');
    }
  }

  private initFilterMenuComponent() {
    const ref = this.componentRef;
    if (!ref?.instance) return;
    if (ref.instance instanceof IndFilterMenuComponent) {
      let component = ref.instance as IndFilterMenuComponent;
      new PropertyWatcher(component)
        .watch('filterMenuSectionHeader', (value) => {
          const omitCreatedOn = (item: any) =>
            normalizeAndStringify(item, ['createdOn']);
          const savedSelectedCategories = (
            component.savedSelectedCategories || []
          )
            .map(omitCreatedOn)
            .sort();
          const tempSelectedcategories = (
            component.tempSelectedcategories || []
          )
            .map(omitCreatedOn)
            .sort();
          const selectedFilter = (component.selectedFilter || [])
            .map(omitCreatedOn)
            .sort();
          const isFilterModified = !isEqual(
            selectedFilter,
            tempSelectedcategories,
          );
          const hasPendingChanges =
            tempSelectedcategories.length > 0 &&
            !isEqual(savedSelectedCategories, tempSelectedcategories);
          buttons[2].disabled = !hasPendingChanges;
          buttons[3].disabled = tempSelectedcategories.length === 0;
          buttons[4].disabled = !isFilterModified;
          this.buttons = [...buttons];
          this.title = value.title;
          this._cd.markForCheck();
        })
        .watch('isRightPaneNavActive', (value) => {
          buttons[0].hidden = !value;
          this.buttons = [...buttons];
          this._cd.markForCheck();
        });

      let searchHidden = true;
      ref.setInput('searchHidden', searchHidden);
      const buttons = [
        {
          key: 'goBack',
          iconName: 'arrow-back',
          align: 'left' as const,
          hidden: true,
          header: true,
          action: async () => {
            component.onCloseRightPane();
            return false;
          },
        },
        {
          key: 'search',
          icon: 'assets/imgs/search-icon.svg',
          selected: !searchHidden,
          header: true,
          action: async () => {
            searchHidden = !searchHidden;
            buttons[1].selected = !searchHidden;
            ref.setInput('searchHidden', searchHidden);
            (
              this.embeddedViewRef?.rootNodes?.[0] as HTMLElement
            )?.classList.toggle('search-visible', !searchHidden);
            this.buttons = [...buttons];
            if (searchHidden) {
              component.onCloseRightPane();
              component.searchInput = '';
              component.searchText({ target: { value: '' } } as any);
            }
            this._cd.markForCheck();
            return false;
          },
        },
        {
          key: 'save',
          label: 'Save Filter',
          // align: 'left' as const,
          disabled: false,
          action: async () => {
            component.footerButtonClicked('saveFilter');
            return false;
          },
        },
        {
          key: 'cancel',
          label: 'Clear Filter',
          disabled: false,
          action: async () => {
            component.onSectionHeaderControlClick('clear-filter');
            return false;
          },
        },
        {
          key: 'ok',
          label: 'Apply',
          primary: true,
          disabled: false,
          action: async () => {
            component.goBack(true);
            return false;
          },
        },
      ];
      const saveFilterButton = find(this.footerService.actionButtons, [
        'id',
        'saveFilter',
      ]);
      new PropertyWatcher(saveFilterButton).watch(
        'isactive',
        (value: boolean) => {
          buttons[2].disabled = !value;
          this.buttons = [...buttons];
          this._cd.markForCheck();
        },
      );
    }
  }

  ngOnDestroy() {
    this._destroy$.next();
    this._destroy$.complete();
    this.componentRef?.destroy();
  }

  async onAction(button: {
    key: string;
    action: (component: any) => Promise<boolean> | boolean;
  }) {
    let result: boolean | Promise<boolean> = true;
    const component = this.componentRef?.instance;
    if (
      component?.onFooterAction &&
      typeof component?.onFooterAction === 'function'
    ) {
      result = component.onFooterAction.call(component, button.key);
    } else if (button.action) {
      result = button.action(component || this.embeddedViewRef);
    }
    if (result instanceof Promise) {
      result = await result;
    }
    if (result) {
      await this.modalCtrl.dismiss(result, button.key);
    }
  }
}

class PropertyWatcher<T> {
  private readonly valueMap = new Map<keyof T, any>();
  constructor(public object: T) {}
  watch<K extends keyof T>(key: K, watcher: (value: T[K]) => void) {
    Object.defineProperty(this.object, key, {
      get: () => this.valueMap.get(key),
      set: (value: any) => {
        const oldValue = this.valueMap.get(key);
        if (oldValue !== value) {
          this.valueMap.set(key, value);
          watcher(value);
        }
      },
    });
    return this;
  }
}
